import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { applyPboMatch, dedupeGolfers, fetchPboMatchPreview } from "@/lib/contestApi";

const fmtMoney = (n) => (typeof n === "number" ? `$${n.toLocaleString()}` : "—");

const Confidence = ({ c }) => {
  if (c == null) return null;
  const [cls, label] =
    c >= 1 ? ["bg-emerald-500", "exact"] : c >= 0.7 ? ["bg-pbo-gold", "likely"] : ["bg-amber-400", "guess"];
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-pbo-muted" title={`Suggested match (${label})`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cls}`} /> {label}
    </span>
  );
};

// Host confirms golfer → PBO player mapping. Existing golfer ids/names/salaries
// are untouched so already-submitted lineups keep working.
export const PboMatchPanel = ({ state, hostPid, tournamentId, onDone, onCancel }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [mapping, setMapping] = useState({});
  const [addUnmatched, setAddUnmatched] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPreview(null);
    setError("");
    fetchPboMatchPreview(state.code, hostPid, tournamentId)
      .then((p) => {
        setPreview(p);
        const m = {};
        for (const g of p.golfers) m[g.id] = g.current_player_id || g.suggested_player_id || "";
        setMapping(m);
        setAddUnmatched(p.golfers.length === 0);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Couldn't load PBO roster"));
  }, [state.code, hostPid, tournamentId]);

  const usedBy = useMemo(() => {
    const m = {};
    for (const [gid, pid] of Object.entries(mapping)) if (pid) m[pid] = gid;
    return m;
  }, [mapping]);

  const unmatchedPlayers = useMemo(
    () => (preview ? preview.players.filter((p) => !usedBy[p.id]) : []),
    [preview, usedBy]
  );
  const dupCount = useMemo(() => {
    const seen = {};
    let n = 0;
    for (const g of state.golfers || []) {
      if (!g.pbo_player_id) continue;
      if (seen[g.pbo_player_id]) n += 1;
      seen[g.pbo_player_id] = true;
    }
    return n;
  }, [state.golfers]);

  const apply = async () => {
    setBusy(true);
    try {
      const clean = {};
      for (const [gid, pid] of Object.entries(mapping)) clean[gid] = pid || null;
      const res = await applyPboMatch(state.code, hostPid, tournamentId, clean, addUnmatched);
      const bits = [];
      if (res.matched) bits.push(`${res.matched} matched`);
      if (res.added) bits.push(`${res.added} added from PBO`);
      if (res.unmatched_golfers) bits.push(`${res.unmatched_golfers} still unmatched (won't score)`);
      toast.success(`Linked to PBO · ${bits.join(" · ") || "no changes"}`);
      if (!res.link_ok) toast.warning(`Room wasn't registered in the PBO app: ${res.link_error}`, { duration: 8000 });
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save matches");
    } finally {
      setBusy(false);
    }
  };

  const dedupe = async () => {
    setBusy(true);
    try {
      const res = await dedupeGolfers(state.code, hostPid);
      toast.success(res.removed ? `Removed ${res.removed} unpicked duplicate${res.removed === 1 ? "" : "s"}` : "No duplicates to remove");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove duplicates");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5" data-testid="pbo-match-error">
        {error}
      </div>
    );
  }
  if (!preview) {
    return (
      <div className="flex items-center gap-2 text-xs text-pbo-muted py-3" data-testid="pbo-match-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Comparing your field with PBO…
      </div>
    );
  }

  const unmatchedGolfers = preview.golfers.filter((g) => !mapping[g.id]).length;

  return (
    <div className="space-y-3" data-testid="pbo-match-panel">
      {preview.golfers.length > 0 ? (
        <>
          <div className="text-[11px] text-pbo-muted leading-relaxed">
            Match each golfer in your field to their PBO player. Names, salaries and submitted lineups
            stay exactly as they are — only the scoring link is added.
          </div>
          <ul className="divide-y divide-pbo-border border border-pbo-border rounded-lg bg-white max-h-[40vh] overflow-y-auto">
            {preview.golfers.map((g) => (
              <li key={g.id} className="flex items-center gap-2 px-2.5 py-2" data-testid={`match-row-${g.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-pbo-ink truncate">
                    {g.name}
                    {g.picked && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-pbo-gold font-semibold">picked</span>}
                  </div>
                  <div className="text-[10px] text-pbo-muted flex items-center gap-2">
                    {fmtMoney(g.salary)}
                    {mapping[g.id] && mapping[g.id] === g.suggested_player_id && !g.current_player_id && <Confidence c={g.confidence} />}
                    {g.current_player_id && mapping[g.id] === g.current_player_id && (
                      <span className="text-[10px] text-pbo-green-soft">already linked</span>
                    )}
                  </div>
                </div>
                <select
                  value={mapping[g.id] || ""}
                  onChange={(e) => setMapping((m) => ({ ...m, [g.id]: e.target.value }))}
                  className={`h-8 w-[48%] min-w-0 shrink-0 rounded-md border bg-white px-1.5 text-xs text-pbo-ink truncate ${
                    mapping[g.id] ? "border-pbo-border" : "border-amber-400"
                  }`}
                  data-testid={`match-select-${g.id}`}
                >
                  <option value="">— not in PBO —</option>
                  {preview.players.map((p) => (
                    <option key={p.id} value={p.id} disabled={usedBy[p.id] && usedBy[p.id] !== g.id}>
                      {p.name}
                      {p.handicap != null ? ` (${p.handicap})` : ""}
                      {usedBy[p.id] && usedBy[p.id] !== g.id ? " · taken" : ""}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
          {unmatchedGolfers > 0 && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="pbo-match-unmatched-warning">
              {unmatchedGolfers} golfer{unmatchedGolfers === 1 ? "" : "s"} not matched — they&apos;ll score 0 until linked.
            </div>
          )}
        </>
      ) : (
        <div className="text-[11px] text-pbo-muted">Your field is empty — import the whole PBO roster below.</div>
      )}

      <label className="flex items-start gap-2 text-xs text-pbo-ink cursor-pointer">
        <input
          type="checkbox"
          checked={addUnmatched}
          onChange={(e) => setAddUnmatched(e.target.checked)}
          className="mt-0.5 accent-pbo-green"
          data-testid="pbo-match-add-unmatched"
        />
        <span>
          Add the {unmatchedPlayers.length} PBO player{unmatchedPlayers.length === 1 ? "" : "s"} not matched to anyone
          {unmatchedPlayers.length > 0 && (
            <span className="text-pbo-muted"> ({unmatchedPlayers.slice(0, 6).map((p) => p.name).join(", ")}{unmatchedPlayers.length > 6 ? "…" : ""})</span>
          )}
          <span className="block text-[10px] text-pbo-muted">New golfers get a handicap-based starter salary you can edit.</span>
        </span>
      </label>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          onClick={apply}
          disabled={busy}
          className="h-9 text-xs bg-pbo-green hover:bg-pbo-green-deep text-pbo-cream"
          data-testid="pbo-match-apply-btn"
        >
          {busy ? "Saving…" : preview.golfers.length ? "Save matches & link" : `Import ${preview.players.length} players & link`}
        </Button>
        {dupCount > 0 && (
          <Button type="button" variant="outline" onClick={dedupe} disabled={busy} className="h-9 text-xs" data-testid="pbo-dedupe-btn">
            Remove {dupCount} unpicked duplicate{dupCount === 1 ? "" : "s"}
          </Button>
        )}
        <button type="button" onClick={onCancel} className="text-xs text-pbo-muted hover:text-pbo-ink" data-testid="pbo-match-cancel-btn">
          Cancel
        </button>
      </div>
    </div>
  );
};
